/**
 * The browser side of Django-issued sign-in (AMP-770).
 *
 * A session is the access/refresh pair the API issued plus the profile `/me/`
 * returned for that access token, written to localStorage as one record. The
 * profile is only ever stored next to the tokens it was fetched with, so the
 * role, email and analytics id the app reads always describe the account the
 * tokens authenticate.
 *
 * Refresh tokens rotate, and presenting one that was already rotated revokes
 * the whole session server-side (`native_auth/sessions.py`). Every tab shares
 * the record, so two tabs refreshing at once would sign the user out. Refresh
 * therefore runs under a Web Lock that all tabs of the origin share, and
 * re-reads the record once it holds the lock: if another tab already rotated,
 * it uses that tab's tokens instead of spending the refresh token again. Within
 * a tab, concurrent callers also share one in-flight refresh, which is all the
 * protection a browser without Web Locks gets.
 *
 * The re-read is not enough on its own. localStorage is not read-your-writes
 * across tabs: a tab can take the lock before another tab's write reaches it,
 * and send the refresh token that tab just spent (AMP-774). The API answers
 * that token, for a short while after the rotation, with a 401 carrying
 * `refresh_token_superseded` and leaves the session live. The tab then waits
 * for the rotated record to arrive and carries on with it.
 *
 * Impersonation (AMP-773) signs the browser in as another account without
 * ending the admin's session: `startSession(pair, { impersonating: true })`
 * sets the admin's session aside under its own key, and `endImpersonation`
 * puts it back. Nothing else survives the swap in either direction.
 */
import { authLogoutCreate, authRefreshCreate } from '~/data/generated/endpoints/auth/auth';
import { meRetrieve } from '~/data/generated/endpoints/me/me';
import type { AuthRefreshRefused, AuthTokenPair, UserDetail } from '~/data/generated/model';
import { LiftAPIClientError } from '~/data/liftApiMutator';
import type { UserRole } from '~/data/schemas/user';

const SESSION_STORAGE_KEY = 'liftai.authSession';
// The admin's own session while they impersonate someone else. Written after
// the clear that starts an impersonation.
const IMPERSONATOR_STORAGE_KEY = 'liftai.impersonator';
// Refresh tokens of sessions this browser gave up on without the API
// confirming they were revoked. Clearing the browser session leaves this key,
// so `revokePendingSessions` can try again on the next load.
const PENDING_REVOKE_STORAGE_KEY = 'liftai.pendingRevoke';
const KEPT_ON_SIGN_OUT = [PENDING_REVOKE_STORAGE_KEY];
const REFRESH_LOCK_NAME = 'liftai.authRefresh';

// Refresh this long before the access token's `exp`, so a request (or a chat
// stream, which cannot be retried after a 401) never leaves with a token about
// to lapse in flight.
const EXPIRY_MARGIN_SECONDS = 60;

// Refresh and logout run while holding the refresh lock, which every tab and
// sign-out wait on. A request stalled on a dead network must not hold it
// forever, so both give up after this long.
const LOCKED_REQUEST_TIMEOUT_MS = 15_000;

// After a refresh is refused as superseded, how long to wait for the tab that
// rotated to have its record show up here. Cross-tab writes normally arrive
// within milliseconds; this bounds the wait when none is coming. It is well
// inside the API's 30-second grace window.
const SUPERSEDED_WAIT_MS = 5_000;

// Typed from the generated schema, so a renamed code fails the build. A string
// rather than the generated enum object, which would pull the whole generated
// model index into this module at runtime.
const REFRESH_TOKEN_SUPERSEDED: NonNullable<AuthRefreshRefused['code']> =
  'refresh_token_superseded';

// The auth endpoints run no authenticator, and the mutator skips its own token
// lookup when a request already carries an Authorization header. Sending an
// empty one keeps a refresh or logout from first trying to refresh itself.
const ANONYMOUS: RequestInit = { headers: { Authorization: '' } };

/** The account a stored session belongs to, as `/me/` described it. */
export type SessionUser = {
  /** The Django user id. Also the analytics and feature-flag identity. */
  id: string;
  email: string;
  role: UserRole;
  isLiftaiAdmin: boolean;
};

export type StoredSession = AuthTokenPair & { user: SessionUser };

// Callers tell these apart by `name`.

/** No session is stored in this browser. */
class NotSignedInError extends Error {
  constructor() {
    super('User is not logged in');
    this.name = 'NotSignedInError';
  }
}

/** The API refused the refresh token, so the session is over. */
class SessionEndedError extends Error {
  constructor() {
    super('Your session has ended. Please sign in again.');
    this.name = 'SessionEndedError';
  }
}

/**
 * The stored session now belongs to a different account than the token that
 * was rejected (another tab signed out and in as someone else). The rejected
 * request must not be retried as that other account.
 */
class SessionChangedError extends Error {
  constructor() {
    super('You signed in as a different user in another tab. Reload the page to continue.');
    this.name = 'SessionChangedError';
  }
}

const isString = (value: unknown): value is string => typeof value === 'string';

const parseSession = (raw: string | null): StoredSession | null => {
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as Partial<StoredSession> | null;
    const user = parsed?.user;
    if (
      !parsed ||
      !isString(parsed.access) ||
      !isString(parsed.refresh) ||
      !user ||
      !isString(user.id) ||
      !isString(user.email)
    ) {
      return null;
    }
    return parsed as StoredSession;
  } catch {
    return null;
  }
};

export const readSession = (): StoredSession | null =>
  parseSession(localStorage.getItem(SESSION_STORAGE_KEY));

const readImpersonator = (): StoredSession | null =>
  parseSession(localStorage.getItem(IMPERSONATOR_STORAGE_KEY));

/**
 * The account this document loaded as, or last signed in as itself. Loaders,
 * rendered data and the analytics, Sentry and feature-flag identity are all set
 * for this account, so its requests must not go out with another account's
 * token. `null` while this document is signed out.
 */
let documentUserId: string | null = readSession()?.user.id ?? null;

const writeSession = (session: StoredSession) => {
  localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
};

/**
 * Reads a claim from one of our JWTs without verifying it. Only used to
 * schedule refreshes and to compare two tokens this browser stored; the API
 * verifies every token it receives.
 */
const readClaim = (token: string, claim: 'exp' | 'user_id' | 'sid'): unknown => {
  try {
    const payload = token.split('.')[1];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return (JSON.parse(json) as Record<string, unknown>)[claim];
  } catch {
    return undefined;
  }
};

const expiresWithin = (token: string, seconds: number) => {
  const exp = readClaim(token, 'exp');
  return typeof exp === 'number' && exp * 1000 - Date.now() <= seconds * 1000;
};

const sameAccount = (a: string, b: string) => {
  const userA = readClaim(a, 'user_id');
  return isString(userA) && userA === readClaim(b, 'user_id');
};

const sameSession = (a: string, b: string) => {
  const sessionA = readClaim(a, 'sid');
  return isString(sessionA) && sessionA === readClaim(b, 'sid');
};

/** An impersonation is already active; it has to end before another starts. */
class AlreadyImpersonatingError extends Error {
  constructor() {
    super('Exit impersonation before impersonating another user.');
    this.name = 'AlreadyImpersonatingError';
  }
}

class LockedRequestTimeoutError extends Error {
  constructor() {
    super('The request took too long. Check your connection and try again.');
    this.name = 'LockedRequestTimeoutError';
  }
}

/** Settle with `request`, or reject once the timeout passes, whichever comes first. */
const withTimeout = <T>(request: (signal: AbortSignal) => Promise<T>): Promise<T> => {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      controller.abort();
      reject(new LockedRequestTimeoutError());
    }, LOCKED_REQUEST_TIMEOUT_MS);
  });
  return Promise.race([request(controller.signal), timeout]).finally(() => clearTimeout(timer));
};

const withRefreshLock = async <T>(task: () => Promise<T>): Promise<T> => {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
  return locks ? await locks.request(REFRESH_LOCK_NAME, task) : task();
};

type SessionEndedListener = () => void;
const sessionEndedListeners = new Set<SessionEndedListener>();

/** Called after a refresh is refused and the local session has been cleared. */
export const onSessionEnded = (listener: SessionEndedListener) => {
  sessionEndedListeners.add(listener);
  return () => {
    sessionEndedListeners.delete(listener);
  };
};

type SessionChangedElsewhereListener = (user: SessionUser | null) => void;

/**
 * Called when another tab leaves a different account signed in than the one
 * this document is showing (a sign-in, an impersonation starting or ending), or
 * signs out, with the account now stored. A refresh that keeps the same account
 * does not count.
 */
export const onSessionChangedElsewhere = (listener: SessionChangedElsewhereListener) => {
  const handleStorage = (event: StorageEvent) => {
    // `key` is null when another tab cleared all of localStorage.
    if (event.storageArea !== localStorage) {
      return;
    }
    if (event.key !== SESSION_STORAGE_KEY && event.key !== null) {
      return;
    }
    const user = readSession()?.user ?? null;
    if ((user?.id ?? null) !== documentUserId) {
      listener(user);
    }
  };
  window.addEventListener('storage', handleStorage);
  return () => window.removeEventListener('storage', handleStorage);
};

// Where the Firebase SDK kept its sign-in (the browser persistence it defaults
// to). The webapp signed in with Firebase until AMP-770.
const FIREBASE_AUTH_DATABASE = 'firebaseLocalStorageDb';

/**
 * Delete the sign-in the Firebase-era webapp left in IndexedDB. Clearing
 * localStorage does not reach it, and this bundle never signs out of Firebase,
 * so on a shared computer its refresh token would stay usable against the
 * Firebase project. Best effort: never throws, and does nothing where IndexedDB
 * is unavailable.
 */
export const deleteFirebaseAuthStorage = () => {
  try {
    if (typeof indexedDB !== 'undefined') {
      indexedDB.deleteDatabase(FIREBASE_AUTH_DATABASE);
    }
  } catch {
    // Blocked or unsupported (some private modes). Nothing else to try.
  }
};

const clearBrowserSession = () => {
  // Not just the session record: several localStorage keys are global rather
  // than per-user (Otto chat history, invoice recipient lists, the saved period
  // filter), and the next person to sign in on this browser must not see them.
  // Sessions still waiting to be revoked are kept, and so is the What's New
  // seen date, so signing back in doesn't show old updates as new.
  const kept = KEPT_ON_SIGN_OUT.map((key) => [key, localStorage.getItem(key)] as const);
  localStorage.clear();
  for (const [key, value] of kept) {
    if (value) localStorage.setItem(key, value);
  }
  deleteFirebaseAuthStorage();
};

const readPendingRevokes = (): string[] => {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(PENDING_REVOKE_STORAGE_KEY) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter(isString) : [];
  } catch {
    return [];
  }
};

const writePendingRevokes = (refreshTokens: string[]) => {
  if (refreshTokens.length) {
    localStorage.setItem(PENDING_REVOKE_STORAGE_KEY, JSON.stringify(refreshTokens));
  } else {
    localStorage.removeItem(PENDING_REVOKE_STORAGE_KEY);
  }
};

const addPendingRevokes = (refreshTokens: string[]) => {
  writePendingRevokes([...new Set([...readPendingRevokes(), ...refreshTokens])]);
};

const removePendingRevoke = (refresh: string) => {
  writePendingRevokes(readPendingRevokes().filter((pending) => pending !== refresh));
};

/**
 * Ask the API to revoke the session `refresh` belongs to. The first attempt is
 * sent with `keepalive`, so it still goes out if the tab navigates away or
 * closes. The retry is a plain request: `keepalive` on a cross-origin JSON POST
 * needs a CORS preflight, and browsers that cannot combine the two reject it,
 * which would otherwise fail every revoke. No retry after a timeout, since a
 * second one would double how long a sign-out waits and the pending record
 * covers it. A 401 counts as revoked: that session is already over.
 */
const sendRevoke = async (refresh: string): Promise<boolean> => {
  for (let attempt = 0; ; attempt += 1) {
    try {
      const keepalive = attempt === 0;
      await withTimeout((signal) =>
        authLogoutCreate({ refresh }, { ...ANONYMOUS, signal, keepalive }),
      );
      return true;
    } catch (error) {
      if (error instanceof LiftAPIClientError && error.context.status === 401) {
        return true;
      }
      if (error instanceof LockedRequestTimeoutError || attempt >= 1) {
        return false;
      }
    }
  }
};

/**
 * Revoke the session `refresh` belongs to, for every session this browser
 * drops. Resolves to whether the API confirmed it. The token is recorded under
 * PENDING_REVOKE_STORAGE_KEY before the request and removed once it is
 * confirmed, so a failed or interrupted revoke is tried again on the next load
 * (`revokePendingSessions`) rather than leaving the session live.
 */
const revoke = async (refresh: string): Promise<boolean> => {
  addPendingRevokes([refresh]);
  const revoked = await sendRevoke(refresh);
  if (revoked) {
    removePendingRevoke(refresh);
  }
  return revoked;
};

/**
 * Try again to revoke the sessions an earlier page gave up on. Call once the
 * SDK is configured, when the app loads.
 *
 * A pending token from the session this browser is still signed in to, whose
 * stored refresh token has since moved on, is dropped without revoking: the
 * rotation that page gave up waiting for was this browser's own, and the tab
 * that made it closed before the new pair arrived.
 */
export const revokePendingSessions = async () => {
  const stored = readSession();
  await Promise.all(
    readPendingRevokes().map(async (refresh) => {
      const ownRotation =
        stored !== null && stored.refresh !== refresh && sameSession(stored.refresh, refresh);
      if (ownRotation || (await sendRevoke(refresh))) {
        removePendingRevoke(refresh);
      }
    }),
  );
};

/** The API refused the refresh token because another tab spent it moments ago. */
const isSupersededRefusal = (error: LiftAPIClientError) =>
  (error.context.data as Partial<AuthRefreshRefused> | null)?.code === REFRESH_TOKEN_SUPERSEDED;

/**
 * Wait for the stored record to stop carrying `refresh`, as it does once
 * another tab's rotated pair reaches this tab. Resolves with the record then
 * (null if it was cleared instead), or `undefined` if nothing changed within
 * SUPERSEDED_WAIT_MS.
 */
const waitForNewerSession = (refresh: string) =>
  new Promise<StoredSession | null | undefined>((resolve) => {
    const onStorage = (event: StorageEvent) => {
      // A null key is another tab's localStorage.clear().
      if (event.key === SESSION_STORAGE_KEY || event.key === null) {
        check();
      }
    };
    const finish = (session: StoredSession | null | undefined) => {
      clearTimeout(timer);
      window.removeEventListener('storage', onStorage);
      resolve(session);
    };
    const check = () => {
      const current = readSession();
      if (current?.refresh === refresh) {
        return false;
      }
      finish(current);
      return true;
    };
    window.addEventListener('storage', onStorage);
    const timer = setTimeout(() => {
      if (!check()) {
        finish(undefined);
      }
    }, SUPERSEDED_WAIT_MS);
    // The record may already have changed. Checked in the same task as adding
    // the listener, so no write can slip in between.
    check();
  });

const rotate = async (rejectedAccess: string, afterSuperseded = false): Promise<string> => {
  const session = readSession();
  if (!session) {
    throw new NotSignedInError();
  }
  // The stored token itself is the same account even when it carries no
  // readable claims, as a token that is not a JWT does.
  if (session.access !== rejectedAccess && !sameAccount(session.access, rejectedAccess)) {
    throw new SessionChangedError();
  }
  // Another tab (or an earlier caller in this one) rotated while we waited for
  // the lock. Its token is the current one; spending our refresh token again
  // would revoke the session.
  if (session.access !== rejectedAccess && !expiresWithin(session.access, EXPIRY_MARGIN_SECONDS)) {
    return session.access;
  }

  let pair: AuthTokenPair;
  try {
    pair = (
      await withTimeout((signal) =>
        authRefreshCreate({ refresh: session.refresh }, { ...ANONYMOUS, signal }),
      )
    ).data as AuthTokenPair;
  } catch (error) {
    if (error instanceof LiftAPIClientError && error.context.status === 401) {
      // Sessions to revoke as this one ends. The one the API refused outright
      // is already over.
      const revoking: string[] = [];
      if (isSupersededRefusal(error)) {
        // Recorded before waiting, so a tab closed during the wait still leaves
        // it for the next load (`revokePendingSessions`).
        addPendingRevokes([session.refresh]);
        // Waits once. A second refusal while this tab holds the lock means no
        // lock is shared here, and it ends the session like the first would.
        const newer = afterSuperseded ? undefined : await waitForNewerSession(session.refresh);
        if (newer !== undefined) {
          removePendingRevoke(session.refresh);
          if (newer === null) {
            throw new NotSignedInError();
          }
          // Starts over from the newer record: its access token when it is
          // still fresh, otherwise a refresh with its refresh token. The
          // account check there still applies.
          return rotate(rejectedAccess, true);
        }
        // No tab here holds the pair the API rotated to, so someone else may.
        // The API left the session live; revoke it, as it would have revoked
        // it for the reuse without the grace window. The logout endpoint
        // accepts a rotated refresh token.
        revoking.push(session.refresh);
      }
      if (readSession()?.refresh === session.refresh) {
        // Clearing also drops the admin's set-aside session mid-impersonation,
        // and a session with no copy left in the browser should not stay live.
        const impersonator = readImpersonator();
        if (impersonator) {
          revoking.push(impersonator.refresh);
        }
        addPendingRevokes(revoking);
        clearBrowserSession();
        documentUserId = null;
        sessionEndedListeners.forEach((listener) => listener());
      }
      await Promise.all(revoking.map(revoke));
      throw new SessionEndedError();
    }
    // A network failure, timeout or 5xx leaves the session as it was. The server may
    // have rotated anyway; if it did, the next refresh is refused and the user
    // signs in again, which no client-side scheme can avoid.
    throw error;
  }

  // Signed out, or signed in again, while the request was out. Writing the
  // rotated pair back would resurrect a session the user just ended.
  const current = readSession();
  if (current?.refresh !== session.refresh) {
    if (current && sameAccount(current.access, rejectedAccess)) {
      return current.access;
    }
    throw current ? new SessionChangedError() : new NotSignedInError();
  }
  writeSession({ ...session, access: pair.access, refresh: pair.refresh });
  return pair.access;
};

const inFlight = new Map<string, Promise<string>>();

/**
 * Exchange the refresh token for a new pair after `rejectedAccess` stopped
 * working, and return the access token to retry with. Safe to call from any
 * number of callers and tabs at once: at most one refresh request is made per
 * rotation.
 */
export const refreshAccessToken = (rejectedAccess: string): Promise<string> => {
  let pending = inFlight.get(rejectedAccess);
  if (!pending) {
    pending = withRefreshLock(() => rotate(rejectedAccess)).finally(() => {
      inFlight.delete(rejectedAccess);
    });
    inFlight.set(rejectedAccess, pending);
  }
  return pending;
};

/** The access token to send, refreshed first when it is about to expire. */
export const getAccessToken = async (): Promise<string> => {
  const session = readSession();
  if (!session) {
    throw new NotSignedInError();
  }
  // Another tab changed accounts and this document has not reloaded yet.
  if (session.user.id !== documentUserId) {
    throw new SessionChangedError();
  }
  if (!expiresWithin(session.access, EXPIRY_MARGIN_SECONDS)) {
    return session.access;
  }
  return refreshAccessToken(session.access);
};

const toSessionUser = (me: UserDetail): SessionUser => ({
  id: me.id,
  email: me.email ?? '',
  role: me.role ?? 'none',
  isLiftaiAdmin: me.isLiftaiAdmin,
});

const signInAs = async (pair: AuthTokenPair, user: SessionUser) => {
  const replaced = [readSession(), readImpersonator()].filter((session) => session !== null);
  try {
    if (readSession()?.user.id !== user.id) {
      clearBrowserSession();
      documentUserId = null;
    } else {
      // Signing in on its own ends any impersonation this account was part of.
      localStorage.removeItem(IMPERSONATOR_STORAGE_KEY);
    }
    writeSession({ access: pair.access, refresh: pair.refresh, user });
    documentUserId = user.id;
  } finally {
    // As in `endSession`: a session with no copy left in the browser should not
    // stay live. That covers the session this sign-in replaced, including an
    // impersonation session, and a set-aside admin session it dropped.
    const kept = [readSession()?.refresh, readImpersonator()?.refresh, pair.refresh];
    await Promise.all(
      replaced
        .filter((session) => !kept.includes(session.refresh))
        .map((session) => revoke(session.refresh)),
    );
  }
};

const signInImpersonating = async (pair: AuthTokenPair, user: SessionUser) => {
  if (readImpersonator()) {
    throw new AlreadyImpersonatingError();
  }
  const impersonator = readSession();
  if (!impersonator) {
    throw new NotSignedInError();
  }
  // Another tab signed in as someone else while `/me/` was in flight. Setting
  // that account aside would resume it, not the admin who asked, on exit.
  if (impersonator.user.id !== documentUserId) {
    throw new SessionChangedError();
  }
  // Clear before the swap. Several keys are global rather than per-user (Otto
  // chat history, invoice recipient lists, the saved period filter) and would
  // otherwise leak into the impersonated session. The admin's session is the
  // only thing kept, which is what lets `endImpersonation` resume it; the
  // admin's other local data does not come back. Sessions still waiting to
  // be revoked are kept too.
  clearBrowserSession();
  documentUserId = null;
  try {
    localStorage.setItem(IMPERSONATOR_STORAGE_KEY, JSON.stringify(impersonator));
    writeSession({ access: pair.access, refresh: pair.refresh, user });
    documentUserId = user.id;
  } catch (error) {
    // Never leave the tab signed in as the target without the stash that marks
    // it as an impersonation, or the stash without the session it belongs to.
    // The admin's session is gone from the browser too, so it is revoked.
    clearBrowserSession();
    await revoke(impersonator.refresh);
    throw error;
  }
};

type StartSessionOptions = {
  /**
   * Keep the current session set aside instead of replacing it, so
   * `endImpersonation` can resume it. Refused while an impersonation is
   * already active.
   */
  impersonating?: boolean;
};

/**
 * Sign this browser in with a pair the API just issued. The profile comes from
 * `/me/` called with that pair's own access token, so it cannot describe a
 * different account. If `/me/` or storing the session fails, the pair is
 * revoked rather than left live and unused, and the browser is not signed in
 * as its account.
 *
 * Local data survives only when the same account signs in again. Anything else
 * clears it, including a browser with no stored session: data from the Firebase
 * era (an impersonation flag, another person's Otto history) has no session
 * record to compare against. An impersonation always clears it. A session the
 * sign-in leaves with no copy in the browser, such as the one it replaces, is
 * revoked.
 */
export const startSession = async (
  pair: AuthTokenPair,
  { impersonating = false }: StartSessionOptions = {},
): Promise<SessionUser> => {
  try {
    const { data } = await meRetrieve({ headers: { Authorization: `Bearer ${pair.access}` } });
    const user = toSessionUser(data);
    // Under the refresh lock, so a rotation another tab has in flight for the
    // previous account finishes before this write instead of landing on top of it.
    await withRefreshLock(async () => {
      if (impersonating) {
        await signInImpersonating(pair, user);
      } else {
        await signInAs(pair, user);
      }
    });
    return user;
  } catch (error) {
    await revoke(pair.refresh);
    throw error;
  }
};

/**
 * Sign this browser out and revoke the session server-side. The local session
 * is cleared even when the revoke request fails, and the result says whether
 * the API confirmed it. A refresh token whose revoke failed stays in
 * localStorage under PENDING_REVOKE_STORAGE_KEY until a later load reaches the
 * API. On a shared computer that leaves a live token readable until then; the
 * trade is that a session someone else may hold still gets revoked after a
 * failed logout.
 *
 * Signing out mid-impersonation also revokes the admin's set-aside session: the
 * stash is cleared with everything else, and a session with no copy left in
 * the browser should not stay live.
 */
export const endSession = (): Promise<{ revoked: boolean }> =>
  withRefreshLock(async () => {
    const sessions = [readSession(), readImpersonator()].filter((session) => session !== null);
    clearBrowserSession();
    documentUserId = null;
    const results = await Promise.all(sessions.map((session) => revoke(session.refresh)));
    return { revoked: results.every(Boolean) };
  });

/** Who is impersonating whom, while an impersonation is active. */
export const readImpersonation = (): { impersonator: SessionUser; target: SessionUser } | null => {
  const impersonator = readImpersonator();
  const session = readSession();
  return impersonator && session ? { impersonator: impersonator.user, target: session.user } : null;
};

/**
 * Stop impersonating and sign the admin back in as themselves. Resolves to the
 * account now signed in, or null when the browser ended up signed out.
 *
 * In order: the impersonation session is revoked server-side, the target's
 * local data is cleared, and the admin's set-aside pair is restored. The admin
 * only counts as signed in once the API accepts a refresh of that pair: its
 * access token has usually expired by now, and the session may have been
 * revoked meanwhile. If the refresh fails, the browser signs out completely,
 * which is also what happens when there is no set-aside session to restore.
 */
export const endImpersonation = async (): Promise<{
  user: SessionUser | null;
  revoked: boolean;
}> => {
  const swapped = await withRefreshLock(async () => {
    const impersonator = readImpersonator();
    if (!impersonator) {
      // Already ended, in another tab or by signing out. Leave whatever session
      // is stored alone: it is not an impersonation.
      return { impersonator: null, current: readSession()?.user ?? null, revoked: true };
    }
    const session = readSession();
    const revoked = session ? await revoke(session.refresh) : true;
    clearBrowserSession();
    try {
      writeSession(impersonator);
    } catch (error) {
      // The stash is already gone, so the admin's session has no copy left.
      documentUserId = null;
      await revoke(impersonator.refresh);
      throw error;
    }
    documentUserId = impersonator.user.id;
    return { impersonator, current: null, revoked };
  });

  const { impersonator, revoked } = swapped;
  if (!impersonator) {
    return { user: swapped.current, revoked };
  }
  try {
    await refreshAccessToken(impersonator.access);
  } catch {
    // Unless another tab has since signed in as someone else, sign out. When the
    // refresh was refused this finds nothing left to revoke.
    const current = readSession();
    if (!current || current.user.id === impersonator.user.id) {
      await endSession();
    }
    return { user: null, revoked };
  }
  return { user: impersonator.user, revoked };
};
